import type { UserTask } from "@/types/userTasks.ts";
import { dueDisplayDate, dueLine } from "./userTaskDue.ts";

/**
 * How a new-chat screen divides its pills between the user's own tasks and
 * prompt suggestions.
 *
 * The rule is the same everywhere and only the room differs: tasks lead, and at
 * least one slot is always left for a suggestion so the screen keeps offering
 * okthx something to take on rather than becoming a list of what is owed.
 */
export type ChipBudget = {
  /** Pills in all. */
  slots: number;
  /** How many of them the user's own tasks may take. Always fewer than `slots`. */
  tasks: number;
};

/** A phone's screen, and a browser narrow enough to be one. */
export const COMPACT_CHIP_BUDGET: ChipBudget = { slots: 3, tasks: 2 };

/** A desktop browser, which has the height for more. */
export const ROOMY_CHIP_BUDGET: ChipBudget = { slots: 5, tasks: 3 };

/**
 * Why a task earned its slot, which is what its colour says: a date that has
 * passed, a date still ahead, or no date at all and simply newly found.
 */
export type UserTaskChipTone = "overdue" | "due" | "new";

/** A task worth a glance, and the word that says why it is here. */
export type UserTaskChip = { task: UserTask; label: string; tone: UserTaskChipTone };

/**
 * How many slots the suggestions may have once the user's tasks have taken
 * theirs. Never negative, and never all of them: `ChipBudget` keeps `tasks`
 * below `slots` so a suggestion always fits.
 */
export function suggestionSlots(budget: ChipBudget, taskCount: number) {
  return Math.max(0, budget.slots - taskCount);
}

/**
 * What a screen reader says for a chip.
 *
 * The tone leads, because on screen it is carried by colour alone and a reader
 * gets none of that — "Overdue. Renew the domain" says in words what the red
 * says at a glance. Shared so the two apps cannot word it differently; each
 * still puts it on its own attribute.
 */
export function chipAccessibleName(chip: UserTaskChip) {
  return `${chip.label}. ${chip.task.title}`;
}

/**
 * The user's open tasks worth a glance, at most `maxTasks`: the soonest
 * due and the newest found, taken in turn so neither kind crowds the other out.
 *
 * Shared because both clients draw this row and the rule that picks it must not
 * drift: a chip the phone shows and the browser does not would read as one of
 * them being wrong about what the user owes.
 */
export function pickUserTaskChips(
  tasks: UserTask[],
  maxTasks: number = COMPACT_CHIP_BUDGET.tasks,
  nowMs: number = Date.now(),
): UserTaskChip[] {
  const open = tasks.filter((t) => t.status === "open" && t.assignee === "user");
  const due = open
    .filter((t) => t.dueMs !== null)
    .sort((a, b) => (a.dueMs ?? 0) - (b.dueMs ?? 0))
    .map((task) => ({ task, ...chipFor(task, nowMs) }));
  const newest = [...open]
    .sort((a, b) => b.createdAtMs - a.createdAtMs)
    .map((task) => ({ task, ...chipFor(task, nowMs) }));

  const chips: UserTaskChip[] = [];
  const seen = new Set<string>();
  for (let i = 0; chips.length < maxTasks && i < Math.max(due.length, newest.length); i++) {
    for (const chip of [due[i], newest[i]]) {
      if (!chip || seen.has(chip.task.id) || chips.length === maxTasks) continue;
      seen.add(chip.task.id);
      chips.push(chip);
    }
  }
  return chips;
}

/**
 * A task's word and tone, from the task itself rather than from which list
 * picked it.
 *
 * A task can be both the soonest due and the newest found, and the two lists
 * are only how it earned a slot. What the chip says has to describe the task:
 * calling a task due on Friday "New" was merely vague when it was a word, and
 * is wrong once a colour is making the claim.
 */
function chipFor(task: UserTask, nowMs: number): { label: string; tone: UserTaskChipTone } {
  if (task.dueMs === null) return { label: "New", tone: "new" };
  if (dueLine(task, nowMs)?.overdue) return { label: "Overdue", tone: "overdue" };
  const on = (dueDisplayDate(task) ?? new Date(nowMs)).toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
  });
  return { label: `Due ${on}`, tone: "due" };
}
